import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './ClassroomTab.module.css';
import { useAuth } from '../contexts/AuthContext';
import CourseStudio from '../components/CourseStudio';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, Settings, PlayCircle, Lock, Users, Crown, Globe, Award, Printer, X, BookOpen, Sparkles, Info } from 'lucide-react';

// Small badge describing a course's access type.
function AccessBadge({ c }) {
  if (c.accessType === 'premium') return <span className="badge badge-accent"><Crown size={12} /> ${Number(c.price || 0).toFixed(0)}</span>;
  if (c.accessType === 'level')   return <span className="badge"><Lock size={12} /> Lvl {c.requiredLevel || 0}</span>;
  if (c.accessType === 'members') return <span className="badge"><Users size={12} /> Members</span>;
  return <span className="badge badge-success"><Globe size={12} /> Free</span>;
}

// Human-readable reason a lesson is locked (sequential / drip).
function lockMessage(lock) {
  if (!lock) return 'Locked';
  if (lock.reason === 'drip' && lock.availableAt) {
    const days = Math.max(0, Math.ceil((new Date(lock.availableAt) - Date.now()) / 86400000));
    return `Unlocks in ${days} day${days === 1 ? '' : 's'}`;
  }
  if (lock.reason === 'sequential') return 'Complete the previous lesson to unlock';
  return 'This lesson is locked';
}

export default function ClassroomTab({ onToast, community, initialCourseId, initialLessonId, activeMembership, searchQuery = '' }) {
  const { user, isAdmin, API, token } = useAuth();
  const location = useLocation();
  const isOwner = user && community && Number(user.id) === Number(community.ownerId);
  const isCommAdmin = isAdmin || activeMembership?.role === 'admin';
  
  // Navigation state
  // 'list' | 'detail' | 'learn'
  const [view, setView] = useState('list');
  const [activeCourse, setActiveCourse] = useState(null);
  const [showCourseInfo, setShowCourseInfo] = useState(false);
  
  // Data
  const [courses, setCourses] = useState([]);
  
  // Learn mode data
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lessonStates, setLessonStates] = useState({});
  const [continueInfo, setContinueInfo] = useState(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCourse, setNewCourse] = useState({ title: '', description: '', accessType: 'free', price: 0, requiredLevel: 1 });
  const [managingCourse, setManagingCourse] = useState(null);
  const [unlockCourse, setUnlockCourse] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [certificate, setCertificate] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowCreateModal(false);
        setUnlockCourse(null);
        setCertificate(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navigate = useNavigate();
  const slug = community?.slug || community?.id || 'c';

  useEffect(() => {
    if (!courses || courses.length === 0) return;
    if (initialCourseId) {
      const cid = parseInt(initialCourseId);
      const course = courses.find(c => c.id === cid);
      if (course) {
        setActiveCourse(course);
        if (!activeCourse || activeCourse.id !== cid || modules.length === 0) {
          fetchCurriculum(cid);
        }
        if (initialLessonId) {
          setActiveLessonId(parseInt(initialLessonId));
          setView('learn');
          setShowCourseInfo(false);
        } else {
          if (course.isEnrolled) {
            setView('learn');
            setShowCourseInfo(true);
          } else {
            setView('detail');
          }
        }
      }
    } else {
      setView('list');
      setActiveCourse(null);
    }
  }, [initialCourseId, initialLessonId, courses, activeCourse?.id, modules.length]);

  useEffect(() => {
    fetchCourses();
    fetchContinue();
  }, [community]);

  useEffect(() => {
    if (location.pathname.endsWith('/classroom')) {
      setView('list');
      setActiveCourse(null);
    }
  }, [location.pathname]);

  async function fetchCourses() {
    if (!community) return;
    try {
      const res = await fetch(`${API}/api/courses?communityId=${community.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCourses(data || []);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchContinue() {
    if (!community) return;
    try {
      const res = await fetch(`${API}/api/courses/continue?communityId=${community.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContinueInfo(data && data.courseId ? data : null);
      }
    } catch (e) { /* ignore */ }
  }

  async function handleCreateCourse(e) {
    e.preventDefault();
    if (!community) return;
    try {
      const res = await fetch(`${API}/api/courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...newCourse, communityId: community.id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create course');
      onToast('Course created successfully!');
      setShowCreateModal(false);
      setNewCourse({ title: '', description: '', price: 0 });
      fetchCourses();
    } catch (err) {
      onToast(err.message);
    }
  }

  async function handleEnroll(courseId) {
    try {
      const res = await fetch(`${API}/api/courses/${courseId}/enroll`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed to enroll'); }
      onToast('Successfully enrolled!');
      await fetchCourses();
      navigate(`/${slug}/classroom/${courseId}`);
    } catch (err) {
      onToast(err.message);
    }
  }

  // Decide what happens when a non-enrolled course card is clicked.
  function openCourse(c) {
    if (c.hasAccess) { navigate(`/${slug}/classroom/${c.id}`); return; }
    if (c.accessType === 'premium') { setUnlockCourse(c); return; }
    if (c.accessType === 'level')   { onToast(`Reach level ${c.requiredLevel || 0} to unlock this course`); return; }
    if (c.accessType === 'members') { onToast('This course is for members only'); return; }
    navigate(`/${slug}/classroom/${c.id}`);
  }

  async function handlePurchase(c) {
    setPurchasing(true);
    try {
      const res = await fetch(`${API}/api/courses/${c.id}/purchase`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Purchase failed'); }
      onToast('Unlocked! You now have access.');
      setUnlockCourse(null);
      await fetchCourses();
      openLearn({ ...c, hasAccess: true, isEnrolled: true });
    } catch (err) { onToast(err.message); }
    finally { setPurchasing(false); }
  }

  async function openLearn(course, startAtLesson = false) {
    // Background enroll if not already recorded in DB
    if (!courses.find(c => c.id === course.id)?.isEnrolled) {
      try {
        await fetch(`${API}/api/courses/${course.id}/enroll`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchCourses();
      } catch (e) {
        console.error(e);
      }
    }
    navigate(`/${slug}/classroom/${course.id}`);
  }

  async function fetchCurriculum(courseId) {
    try {
      const res = await fetch(`${API}/api/courses/${courseId}/learn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP Error ${res.status}`);
      const data = await res.json();
      setModules(data.modules || []);
      setLessons(data.lessons || []);
      setAttachments(data.attachments || []);
      setProgress(data.progress || []);
      const states = data.lessonStates || {};
      setLessonStates(states);

      if (data.lessons && data.lessons.length > 0) {
        // Prefer the first unlocked + incomplete lesson; fall back to first unlocked, then first.
        const progIds = new Set((data.progress || []).map(p => p.lessonId));
        const isLocked = id => (states[id] || states[String(id)])?.locked;
        const target = data.lessons.find(l => !progIds.has(l.id) && !isLocked(l.id))
          || data.lessons.find(l => !isLocked(l.id))
          || data.lessons[0];
        if (!initialLessonId) {
          setActiveLessonId(target.id);
        }
      }
    } catch (e) {
      console.error(e);
      onToast('Failed to load curriculum: ' + e.message);
    }
  }

  async function toggleLesson(lessonId, done) {
    try {
      const res = await fetch(`${API}/api/courses/lessons/${lessonId}/complete`, {
        method: done ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed to update progress'); }
      const data = await res.json().catch(() => ({}));
      if (done) onToast('Marked as not done');
      else if (data.certificate) onToast('🎓 Course complete — certificate earned!');
      else onToast(`Lesson complete! +${data.xp || 10} XP`);
      await fetchCurriculum(activeCourse.id);
      fetchCourses();
      fetchContinue();
    } catch (err) {
      onToast(err.message);
    }
  }

  async function viewCertificate() {
    if (!activeCourse) return;
    try {
      const res = await fetch(`${API}/api/courses/${activeCourse.id}/certificate`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Certificate not available');
      setCertificate(data);
    } catch (err) { onToast(err.message); }
  }

  if (managingCourse) {
    // Guard: only community owner or admin can access the course studio
    if (!isOwner && !isCommAdmin) {
      setManagingCourse(null);
      return null;
    }
    return <CourseStudio course={managingCourse} onBack={() => { setManagingCourse(null); fetchCourses(); }} onToast={onToast} />;
  }

  const myCourses = searchQuery.trim()
    ? courses.filter(c => {
        const q = searchQuery.toLowerCase().trim();
        const title = (c.title || '').toLowerCase();
        const desc = (c.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
      })
    : courses;

  if (view === 'detail' && activeCourse) {
    return (
      <div>
        <div className="card" style={{ maxWidth: 800, margin: '0 auto' }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div className="card-title">{activeCourse.title}</div>
            <AccessBadge c={activeCourse} />
          </div>
          <div className="card-body">
            {activeCourse.coverImageUrl && (
              <div style={{ marginBottom: 24, borderRadius: 12, overflow: 'hidden' }}>
                {(activeCourse.coverImageUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/) ||
                  activeCourse.coverImageUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/) ||
                  activeCourse.coverImageUrl.match(/loom\.com\/(?:share|embed)\/([\w-]+)/) ||
                  /\.(mp4|webm|ogg|mov|m4v)(?:\?|$)/i.test(activeCourse.coverImageUrl)) ? (
                  <VideoEmbed url={activeCourse.coverImageUrl} api={API} />
                ) : (
                  <img 
                    src={/^https?:\/\//.test(activeCourse.coverImageUrl) ? activeCourse.coverImageUrl : `${API}${activeCourse.coverImageUrl}`} 
                    alt="Course Cover" 
                    style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12 }} 
                  />
                )}
              </div>
            )}
            <div style={{ color: 'var(--text2)', lineHeight: 1.6, fontSize: '15px' }}>
              <MarkdownRenderer content={activeCourse.description} />
            </div>
            <div className="divider" />
            {courses.find(c => c.id === activeCourse.id)?.isEnrolled ? (
              <button className="btn btn-primary" onClick={() => navigate(`/${slug}/classroom/${activeCourse.id}/${activeLessonId || ''}`)}>Resume Learning</button>
            ) : (
              <button className="btn btn-primary" onClick={() => handleEnroll(activeCourse.id)}>Enroll Now</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'learn' && activeCourse) {
    const isAuthor  = Number(activeCourse.authorId) === Number(user?.id);
    const canManage = isOwner || isCommAdmin; // Only community owner/admin can manage courses
    const progIds = new Set(progress.map(p => p.lessonId));
    const orderedLessons = modules.flatMap(m => lessons.filter(l => l.moduleId === m.id));
    const total = orderedLessons.length;
    const completed = orderedLessons.filter(l => progIds.has(l.id)).length;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const activeLsn = orderedLessons.find(l => l.id === activeLessonId) || orderedLessons[0];
    const idx = activeLsn ? orderedLessons.findIndex(l => l.id === activeLsn.id) : -1;
    const prevLsn = idx > 0 ? orderedLessons[idx - 1] : null;
    const nextLsn = idx >= 0 && idx < total - 1 ? orderedLessons[idx + 1] : null;
    const isDone = activeLsn ? progIds.has(activeLsn.id) : false;
    const activeLock = activeLsn ? (lessonStates[activeLsn.id] || lessonStates[String(activeLsn.id)]) : null;
    const activeLocked = !!(activeLock && activeLock.locked);

    return (
      <div>
        <div className={styles.courseDetailGrid}>
          {/* Left Sidebar: Curriculum */}
          <div className="flex-col" style={{ gap: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', flex: 1 }}>
                  {activeCourse.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <button 
                    type="button"
                    className="qeduvo-icon-btn" 
                    title="Course Info"
                    style={{ 
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: showCourseInfo ? 'var(--accent)' : (pct === 100 ? 'var(--accent3)' : 'var(--text3)'), 
                      background: showCourseInfo ? 'var(--accent-soft)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'scale(1.08)';
                      e.currentTarget.style.background = showCourseInfo ? 'var(--accent-soft)' : 'var(--surface2)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = showCourseInfo ? 'var(--accent-soft)' : 'transparent';
                    }}
                    onClick={() => navigate(`/${slug}/classroom/${activeCourse.id}`)}
                  >
                    {pct === 100 ? <CheckCircle2 size={20} /> : <Info size={20} />}
                  </button>
                  {canManage && (
                    <button 
                      type="button"
                      className="qeduvo-icon-btn" 
                      title="Manage course"
                      style={{ 
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text3)', 
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.08)';
                        e.currentTarget.style.background = 'var(--surface2)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                      onClick={() => setManagingCourse(activeCourse)}
                    >
                      <Settings size={20} />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent-grad)', transition: 'width .3s' }} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6, color: 'var(--text2)' }}>
                {completed} / {total} lessons · {pct}%
              </div>
              {pct === 100 && (
                <button className="btn btn-primary btn-sm" style={{ marginTop: 12, width: '100%' }} onClick={viewCertificate}>
                  <Award size={15} /> View certificate
                </button>
              )}
            </div>

            {/* Desktop Lesson List */}
            <div className={styles.desktopLessons}>
              {modules.map(m => (
                <div key={m.id} style={{ marginBottom: 16 }}>
                  {modules.length > 1 && (
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingLeft: 12 }}>
                      {m.title}
                    </div>
                  )}
                  <div className={styles.lessonList}>
                    {lessons.filter(l => l.moduleId === m.id).map(l => {
                      const isDone = progIds.has(l.id);
                      const isActive = !showCourseInfo && activeLsn?.id === l.id;
                      const lock = lessonStates[l.id] || lessonStates[String(l.id)];
                      const locked = !!(lock && lock.locked);
                      return (
                        <div
                          className={styles.lessonItem}
                          key={l.id}
                          title={locked ? lockMessage(lock) : undefined}
                          onClick={() => {
                            if (locked) {
                              onToast(lockMessage(lock));
                            } else {
                              navigate(`/${slug}/classroom/${activeCourse.id}/${l.id}`);
                            }
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: isActive ? 'var(--accent-soft)' : 'transparent',
                            color: locked ? 'var(--text3)' : (isActive ? 'var(--accent)' : 'var(--text)'),
                            cursor: locked ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {locked
                            ? <Lock size={15} style={{ color: 'var(--text3)', flexShrink: 0 }} />
                            : isDone
                              ? <CheckCircle2 size={15} style={{ color: 'var(--accent3)', flexShrink: 0 }} />
                              : <Circle size={15} style={{ color: 'var(--text3)', flexShrink: 0 }} />}
                          <div className={styles.lessonTitle} style={{ fontWeight: isActive ? 600 : 400 }}>
                            {l.title}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Dropdown Lesson Combo-box Selector */}
            <div className={styles.mobileLessons}>
              <select
                value={showCourseInfo ? 'info' : (activeLsn?.id || '')}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'info') {
                    navigate(`/${slug}/classroom/${activeCourse.id}`);
                  } else {
                    const lsn = orderedLessons.find(l => String(l.id) === String(val));
                    if (lsn) {
                      const lock = lessonStates[lsn.id] || lessonStates[String(lsn.id)];
                      const locked = !!(lock && lock.locked);
                      if (locked) {
                        onToast(lockMessage(lock));
                      } else {
                        navigate(`/${slug}/classroom/${activeCourse.id}/${lsn.id}`);
                      }
                    }
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 16px',
                  borderRadius: '12px',
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 20px center',
                  backgroundSize: '16px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontWeight: '600',
                  outline: 'none',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-sm)',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
              >
                <option value="info">📋 Course Info & Overview</option>
                {modules.map(m => (
                  <optgroup key={m.id} label={m.title}>
                    {lessons.filter(l => l.moduleId === m.id).map(l => {
                      const isDone = progIds.has(l.id);
                      const lock = lessonStates[l.id] || lessonStates[String(l.id)];
                      const locked = !!(lock && lock.locked);
                      return (
                        <option key={l.id} value={l.id} disabled={locked}>
                          {locked ? '🔒 ' : (isDone ? '✅ ' : '○ ')} {l.title}
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          {/* Right Main Area: Lesson Details */}
          <div className="flex-col">
            {showCourseInfo ? (
              <div className="card" style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 12 }}>
                <div className="card-body" style={{ padding: '30px' }}>
                  {activeCourse.coverImageUrl && (
                    <div style={{ marginBottom: 24, borderRadius: 12, overflow: 'hidden' }}>
                      {(activeCourse.coverImageUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/) ||
                        activeCourse.coverImageUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/) ||
                        activeCourse.coverImageUrl.match(/loom\.com\/(?:share|embed)\/([\w-]+)/) ||
                        /\.(mp4|webm|ogg|mov|m4v)(?:\?|$)/i.test(activeCourse.coverImageUrl)) ? (
                        <VideoEmbed url={activeCourse.coverImageUrl} api={API} />
                      ) : (
                        <img 
                          src={/^https?:\/\//.test(activeCourse.coverImageUrl) ? activeCourse.coverImageUrl : `${API}${activeCourse.coverImageUrl}`} 
                          alt="Course Cover" 
                          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12 }} 
                        />
                      )}
                    </div>
                  )}
                  <div style={{ color: 'var(--text2)', lineHeight: 1.6, fontSize: '15px' }}>
                    <MarkdownRenderer content={activeCourse.description} />
                  </div>
                </div>
              </div>
            ) : activeLsn ? (
              <div className="card" style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 12 }}>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 'none', padding: '24px 30px 10px' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{activeLsn.title}</div>
                  <button 
                    className={styles.statusBtn} 
                    disabled={activeLocked} 
                    onClick={() => !activeLocked && toggleLesson(activeLsn.id, isDone)}
                    title={activeLocked ? lockMessage(activeLock) : (isDone ? 'Completed — click to mark incomplete' : 'Mark as complete')}
                  >
                    {activeLocked ? (
                      <Lock size={24} style={{ color: 'var(--text3)' }} />
                    ) : isDone ? (
                      <CheckCircle2 size={24} style={{ color: 'var(--accent3)' }} />
                    ) : (
                      <Circle size={24} style={{ color: 'var(--text3)' }} />
                    )}
                  </button>
                </div>
                <div className="card-body" style={{ padding: '10px 30px 30px' }}>
                  {activeLsn.videoUrl && (
                    <div style={{ marginBottom: 24 }}>
                      <VideoEmbed url={activeLsn.videoUrl} api={API} />
                    </div>
                  )}
                  {activeLsn.content && (
                    <div style={{ lineHeight: 1.8, color: 'var(--text2)', fontSize: 15 }}>
                      <MarkdownRenderer content={activeLsn.content} />
                    </div>
                  )}
                  
                  {attachments.filter(a => a.lessonId === activeLsn.id).length > 0 && (
                    <div className="mt-24">
                      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>RESOURCES:</div>
                      <div className="flex-col" style={{ gap: 8 }}>
                        {attachments.filter(a => a.lessonId === activeLsn.id).map(a => (
                          <a key={a.id} href={`${API}${a.fileUrl}`} target="_blank" rel="noreferrer" style={{ color: 'var(--text2)', textDecoration: 'none', fontSize: 14 }}>
                            • {a.fileName}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="divider my-24" />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-ghost btn-sm" disabled={!prevLsn} onClick={() => prevLsn && setActiveLessonId(prevLsn.id)}><ChevronLeft size={16} /> Previous</button>
                    <button className="btn btn-ghost btn-sm" disabled={!nextLsn} onClick={() => nextLsn && setActiveLessonId(nextLsn.id)}>Next <ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
              ) : (
              <div className="card" style={{ border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: 12 }}><div className="card-body">Select a lesson to begin.</div></div>
            )}
          </div>
        </div>

        {certificate && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCertificate(null)}>
            <div className="modal" style={{ maxWidth: 680, borderRadius: 24, position: 'relative' }}>
              <button 
                onClick={() => setCertificate(null)}
                style={{ 
                  position: 'absolute', 
                  top: 20, 
                  right: 20, 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: 'var(--text3)',
                  zIndex: 10
                }}
              >
                <X size={20} />
              </button>
              <div className="modal-body" style={{ padding: '40px 28px 28px' }}>
                
                {/* Print styles tag */}
                <style>{`
                  @media print {
                    /* Force background graphics to print */
                    * {
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                    }
                    /* Hide everything in the document */
                    body * {
                      visibility: hidden;
                    }
                    /* Show only the certificate print card and its contents */
                    .certificate-print-area, .certificate-print-area * {
                      visibility: visible;
                    }
                    /* Force layout landscape page specifications */
                    @page {
                      size: landscape;
                      margin: 0;
                    }
                    /* Absolute position the printable certificate card on page */
                    .certificate-print-area {
                      position: fixed !important;
                      left: 0 !important;
                      top: 0 !important;
                      right: 0 !important;
                      bottom: 0 !important;
                      width: 100vw !important;
                      height: 100vh !important;
                      z-index: 9999 !important;
                      margin: 0 !important;
                      padding: 60px 80px !important;
                      background: #ffffff !important;
                      border: 24px double #4f46e5 !important;
                      border-radius: 0 !important;
                      display: flex !important;
                      flex-direction: column !important;
                      justify-content: center !important;
                      align-items: center !important;
                      text-align: center !important;
                    }
                    .certificate-emblem {
                      background: #4f46e5 !important;
                      background-color: #4f46e5 !important;
                      border: 2px solid #4f46e5 !important;
                    }
                    .certificate-emblem svg {
                      color: #ffffff !important;
                      stroke: #ffffff !important;
                    }
                  }
                `}</style>

                {/* Certificate Frame */}
                <div className="certificate-print-area" style={{ 
                  border: '12px double var(--accent)', 
                  borderRadius: 20, 
                  padding: '48px 40px', 
                  textAlign: 'center', 
                  background: 'var(--surface)',
                  boxShadow: 'inset 0 0 0 1px rgba(99, 102, 241, 0.1)',
                  position: 'relative'
                }}>
                  
                  {/* Decorative corner borders */}
                  <div style={{ position: 'absolute', top: 12, left: 12, width: 24, height: 24, borderTop: '3px solid var(--accent)', borderLeft: '3px solid var(--accent)' }} />
                  <div style={{ position: 'absolute', top: 12, right: 12, width: 24, height: 24, borderTop: '3px solid var(--accent)', borderRight: '3px solid var(--accent)' }} />
                  <div style={{ position: 'absolute', bottom: 12, left: 12, width: 24, height: 24, borderBottom: '3px solid var(--accent)', borderLeft: '3px solid var(--accent)' }} />
                  <div style={{ position: 'absolute', bottom: 12, right: 12, width: 24, height: 24, borderBottom: '3px solid var(--accent)', borderRight: '3px solid var(--accent)' }} />

                  {/* Icon Emblem */}
                  <div className="certificate-emblem" style={{ 
                    width: 72, 
                    height: 72, 
                    borderRadius: '50%', 
                    background: 'var(--accent-grad)', 
                    color: '#fff', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 20px -6px var(--accent)',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    <Award size={38} style={{ color: '#fff' }} />
                  </div>

                  <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 3, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 18 }}>
                    Certificate of Completion
                  </div>
                  
                  <div style={{ fontSize: 14, color: 'var(--text2)', fontStyle: 'italic', marginBottom: 8 }}>
                    This is proudly presented to
                  </div>
                  
                  <div style={{ fontSize: 32, fontWeight: 900, color: 'var(--text)', margin: '8px 0 16px', borderBottom: '2px solid var(--border)', display: 'inline-block', paddingBottom: 10, minWidth: 260 }}>
                    {certificate.learnerName}
                  </div>
                  
                  <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 8 }}>
                    for successfully finishing the specialized program
                  </div>
                  
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)', margin: '4px 0 32px' }}>
                    {certificate.courseTitle}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 16, fontSize: 12, color: 'var(--text3)' }}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text2)' }}>Date Issued</div>
                      <div>{new Date(certificate.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text2)' }}>Certificate Identifier</div>
                      <div style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>{certificate.serial}</div>
                    </div>
                  </div>

                </div>

                {/* Print CTA */}
                <button className="btn btn-primary" style={{ width: '100%', marginTop: 20, padding: 14, borderRadius: 14, fontWeight: 800 }} onClick={() => window.print()}>
                  <Printer size={16} /> Print / Save as PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {continueInfo && continueInfo.lessonId && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 18, marginBottom: 20, background: 'var(--accent-soft)', border: '1px solid var(--accent)', cursor: 'pointer' }}
          onClick={() => { const c = courses.find(x => x.id === continueInfo.courseId); if (c) { setActiveLessonId(continueInfo.lessonId); openLearn(c, true); } }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-grad)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PlayCircle size={24} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Continue learning</div>
            <div style={{ fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{continueInfo.courseTitle}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Up next: {continueInfo.lessonTitle} · {continueInfo.percent}%</div>
          </div>
          <ChevronRight size={20} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        </div>
      )}
      <div className="flex-center mb-24" style={{ justifyContent: 'space-between' }}>
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>My Courses</h2>
        <div>
          {user && community && Number(user.id) === Number(community.ownerId) && (
             <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
               + Create Course
             </button>
          )}
        </div>
      </div>
      
      {showCreateModal && (
        <div className="modal-overlay" style={{ backdropFilter: 'blur(8px)', background: 'rgba(2, 6, 23, 0.65)' }} onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="modal" style={{ maxWidth: 520, borderRadius: 24, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
            <button 
              type="button"
              className="qeduvo-icon-btn" 
              onClick={() => setShowCreateModal(false)} 
              style={{ 
                position: 'absolute', 
                top: 16, 
                right: 16, 
                zIndex: 50, 
                width: 32, 
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: 'var(--surface2)',
                color: 'var(--text2)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={16} />
            </button>

            {/* Modal Body */}
            <div className="modal-body" style={{ padding: '28px' }}>
              <form onSubmit={handleCreateCourse} className="flex-col" style={{ gap: 20 }}>
                
                {/* Course Title */}
                <div>
                  <label className="form-label" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Course Title <span style={{ color: 'var(--accent2)' }}>*</span>
                  </label>
                  <input 
                    className="input" 
                    placeholder="e.g. Masterclass in Advanced Design" 
                    required 
                    value={newCourse.title} 
                    onChange={e => setNewCourse({...newCourse, title: e.target.value})} 
                    style={{ borderRadius: 12, padding: '12px 16px' }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="form-label" style={{ marginBottom: 6 }}>
                    Description <span style={{ color: 'var(--accent2)' }}>*</span>
                  </label>
                  <textarea 
                    className="input" 
                    placeholder="Describe what your students will learn in this course..." 
                    required 
                    value={newCourse.description} 
                    onChange={e => setNewCourse({...newCourse, description: e.target.value})} 
                    style={{ minHeight: 90, borderRadius: 12, padding: '12px 16px', resize: 'vertical' }} 
                  />
                </div>

                {/* Access Selection */}
                <div>
                  <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Access Level</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                    {[
                      { v: 'free', label: 'Free', desc: 'Open to everyone', icon: Globe },
                      { v: 'members', label: 'Members Only', desc: 'All community members', icon: Users },
                      { v: 'premium', label: 'Premium', desc: 'Paid one-time access', icon: Crown },
                      { v: 'level', label: 'Level-locked', desc: 'Unlock at certain level', icon: Lock },
                    ].map(opt => {
                      const isActive = newCourse.accessType === opt.v;
                      return (
                        <button 
                          type="button" 
                          key={opt.v} 
                          onClick={() => setNewCourse({ ...newCourse, accessType: opt.v })}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            textAlign: 'left',
                            padding: '12px 14px',
                            borderRadius: 14,
                            border: isActive ? '2px solid var(--accent)' : '1px solid var(--border)',
                            background: isActive ? 'var(--accent-soft)' : 'var(--surface)',
                            boxShadow: isActive ? 'var(--shadow-sm), inset 0 0 0 1px rgba(99, 102, 241, 0.1)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            gap: 4
                          }}
                          className="access-opt-card"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                            <div style={{ 
                              width: 28, 
                              height: 28, 
                              borderRadius: 8, 
                              background: isActive ? 'var(--accent-grad)' : 'var(--surface2)', 
                              color: isActive ? '#fff' : 'var(--text2)',
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center' 
                            }}>
                              <opt.icon size={15} />
                            </div>
                            <span style={{ fontWeight: 700, fontSize: 13.5, color: isActive ? 'var(--accent)' : 'var(--text)' }}>
                              {opt.label}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: isActive ? 'var(--accent-h)' : 'var(--text3)', marginTop: 4, lineHeight: 1.2 }}>
                            {opt.desc}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Conditional Fields */}
                {newCourse.accessType === 'premium' && (
                  <div style={{ 
                    animation: 'fadeIn 0.25s ease', 
                    background: 'var(--bg2)', 
                    padding: 16, 
                    borderRadius: 14, 
                    border: '1px solid var(--border)',
                    marginTop: -4
                  }}>
                    <label className="form-label" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Price (USD) <span style={{ color: 'var(--accent2)' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontWeight: 600, color: 'var(--text2)' }}>$</span>
                      <input 
                        className="input" 
                        type="number" 
                        step="0.01" 
                        min="0" 
                        required
                        value={newCourse.price} 
                        onChange={e => setNewCourse({ ...newCourse, price: parseFloat(e.target.value) || 0 })} 
                        style={{ borderRadius: 10, padding: '10px 16px 10px 28px' }}
                      />
                    </div>
                  </div>
                )}

                {newCourse.accessType === 'level' && (
                  <div style={{ 
                    animation: 'fadeIn 0.25s ease', 
                    background: 'var(--bg2)', 
                    padding: 16, 
                    borderRadius: 14, 
                    border: '1px solid var(--border)',
                    marginTop: -4
                  }}>
                    <label className="form-label" style={{ marginBottom: 6 }}>
                      Required Level to Unlock <span style={{ color: 'var(--accent2)' }}>*</span>
                    </label>
                    <input 
                      className="input" 
                      type="number" 
                      min="1" 
                      required
                      value={newCourse.requiredLevel} 
                      onChange={e => setNewCourse({ ...newCourse, requiredLevel: parseInt(e.target.value) || 1 })} 
                      style={{ borderRadius: 10, padding: '10px 16px' }}
                    />
                  </div>
                )}

                {/* Create Button */}
                <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 10px 20px -10px rgba(99, 102, 241, 0.5)', marginTop: 8 }}>
                  <Sparkles size={16} />
                  <span style={{ fontWeight: 800 }}>Create Course</span>
                </button>

              </form>
            </div>
          </div>
        </div>
      )}

      {unlockCourse && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setUnlockCourse(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Unlock course</div>
              <button className="modal-close" onClick={() => setUnlockCourse(null)}>×</button>
            </div>
            <div className="modal-body flex-col" style={{ gap: 14, textAlign: 'center', alignItems: 'center', padding: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--accent-grad)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Crown size={28} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{unlockCourse.title}</div>
              <div className="text-muted">One-time purchase · lifetime access</div>
              <div style={{ fontSize: 34, fontWeight: 900 }}>${Number(unlockCourse.price || 0).toFixed(2)}</div>
              <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={purchasing} onClick={() => handlePurchase(unlockCourse)}>
                {purchasing ? 'Processing…' : `Unlock for $${Number(unlockCourse.price || 0).toFixed(2)}`}
              </button>
              <div className="text-muted" style={{ fontSize: 11 }}>Demo checkout — no real charge is made.</div>
            </div>
          </div>
        </div>
      )}

      {myCourses.length === 0 && (
        <div className="text-muted mb-24">
          {searchQuery.trim() ? "No courses found matching your search." : "You haven't enrolled in any courses yet."}
        </div>
      )}
      
      <div className={styles.courseGrid}>
        {myCourses.map(c => {
          const total = c.totalLessons || 0;
          const done = c.completedLessons || 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          
          return (
            <div className={styles.courseCard} key={c.id} onClick={() => c.isEnrolled ? openLearn(c) : openCourse(c)}>
              <div className={styles.courseThumb} style={{ background: c.coverImageUrl ? 'none' : 'linear-gradient(135deg, #1f2937, #111827)' }}>
                <CourseCover url={c.coverImageUrl} api={API} />
              </div>
              <div className={styles.courseInfo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <div className={styles.courseTitle}>{c.title}</div>
                  <AccessBadge c={c} />
                </div>
                <div className={styles.courseMeta}>{done}/{total} lessons complete</div>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                </div>
                <div className={styles.progressLabel}>
                  <span>{pct}%</span>
                  <span>{done * 10} XP</span>
                </div>
                {(isOwner || isCommAdmin) && (
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 10, width: '100%' }}
                    onClick={e => { e.stopPropagation(); setManagingCourse(c); }}>Manage course</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

// Renders a lesson video: YouTube / Vimeo / Loom via iframe embed, or an
// uploaded / direct video file via <video>. Wrapped in a 16:9 frame.
function VideoEmbed({ url, api }) {
  if (!url) return null;
  const frame = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 12 };
  const wrap = { position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: 12, overflow: 'hidden' };

  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return <div style={wrap}><iframe style={frame} src={`https://www.youtube.com/embed/${yt[1]}`} title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return <div style={wrap}><iframe style={frame} src={`https://player.vimeo.com/video/${vimeo[1]}`} title="video" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen /></div>;

  const loom = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return <div style={wrap}><iframe style={frame} src={`https://www.loom.com/embed/${loom[1]}`} title="video" allowFullScreen /></div>;

  const src = /^https?:\/\//.test(url) ? url : `${api}${url}`;
  return <div style={wrap}><video src={src} controls style={frame} /></div>;
}

// Renders the course cover which can be a YouTube, Vimeo, Loom, direct video or image.
function CourseCover({ url, api }) {
  if (!url) return <span style={{ fontSize: 52 }}>📖</span>;

  // 1. YouTube
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <img src={`https://img.youtube.com/vi/${yt[1]}/mqdefault.jpg`} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <PlayCircle size={20} style={{ marginLeft: 2 }} />
          </div>
        </div>
      </div>
    );
  }

  // 2. Vimeo
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <img src={`https://vumbnail.com/${vimeo[1]}.jpg`} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <PlayCircle size={20} style={{ marginLeft: 2 }} />
          </div>
        </div>
      </div>
    );
  }

  // 3. Loom
  const loom = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <img src={`https://cdn.loom.com/sessions/thumbnails/${loom[1]}-with-play.gif`} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  // 4. Direct video URL (.mp4, .webm, etc.)
  const isVideo = /\.(mp4|webm|ogg|mov|m4v)(?:\?|$)/i.test(url) || (url.includes('/uploads/') && !/\.(jpg|jpeg|png|webp|gif|svg)(?:\?|$)/i.test(url));
  if (isVideo) {
    const src = /^https?:\/\//.test(url) ? url : `${api}${url}`;
    return (
      <video src={src} muted loop playsInline autoPlay style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    );
  }

  // 5. Direct image URL
  const src = /^https?:\/\//.test(url) ? url : `${api}${url}`;
  return <img src={src} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
}
